import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material';
import { OpenInNew, Security } from '@mui/icons-material';
import { CaptchaTask, checkCookieStatus, openCaptchaUrl } from '../utils/captchaAccess';

interface VerifyAccessDialogProps {
  open: boolean;
  task: CaptchaTask | null;
  onClose: () => void;
  onStatusMessage?: (message: string) => void;
  /** Override default cookie check (e.g. to update parent list state). */
  onCheckStatus?: () => Promise<void>;
}

const VerifyAccessDialog: React.FC<VerifyAccessDialogProps> = ({
  open,
  task,
  onClose,
  onStatusMessage,
  onCheckStatus,
}) => {
  const handleCheckStatus = async () => {
    if (onCheckStatus) {
      await onCheckStatus();
      return;
    }

    const url = task?.urls?.[0];
    if (!url || !task?.id) return;

    const domain = new URL(url).hostname;
    const hasCookies = await checkCookieStatus(domain);
    if (onStatusMessage) {
      onStatusMessage(
        hasCookies
          ? 'Auth export completed! You can now rerun this task.'
          : 'No cookies found. Please complete the auth export first.'
      );
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Security color="warning" />
          Verify Access (Login/CAPTCHA) for {task?.name}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            This task requires login or CAPTCHA verification to access the target website. Follow
            the steps below to complete the process.
          </Alert>

          <Typography variant="h6" gutterBottom>
            Step 1: Install Chrome Extension (if not already installed)
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Install the Authentix Cookie Exporter extension from the Chrome Web Store or load it
            as an unpacked extension.
          </Typography>

          <Typography variant="h6" gutterBottom>
            Step 2: Open the Verification/Login Page
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Click the button below to open the target URL in a new tab. Complete any CAPTCHA or
            login verification on that page.
          </Typography>

          <Button
            variant="contained"
            startIcon={<OpenInNew />}
            onClick={() => {
              if (task) {
                openCaptchaUrl(task);
              }
            }}
            sx={{ mb: 1 }}
          >
            Open Verification/Login Page
          </Button>
          {task?.blocked_url ? (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
              Challenge URL:{' '}
              <Link href={task.blocked_url} target="_blank" rel="noopener noreferrer">
                {task.blocked_url}
              </Link>
            </Typography>
          ) : (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
              The task did not include a challenge URL. The button above will open the original
              task URL.
            </Typography>
          )}
          {task?.challenge_screenshot_url && (
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
              Challenge Screenshot:{' '}
              <Link
                href={task.challenge_screenshot_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Screenshot
              </Link>
            </Typography>
          )}

          <Typography variant="h6" gutterBottom>
            Step 3: Export Cookies
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            After completing the CAPTCHA, click the &quot;Export Cookies&quot; button in the
            extension popup or the notification banner on the page.
          </Typography>

          <Typography variant="h6" gutterBottom>
            Step 4: Verify Success
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Once cookies are exported, you can close this dialog and rerun the task. The scraper
            will now bypass the CAPTCHA.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={handleCheckStatus}>
          Check Status & Enable Rerun
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VerifyAccessDialog;
